import { memo, useMemo, useState, useEffect } from 'react';
import {
    Paper, Table, TableHead, TableRow, TableCell, TableBody, Typography,
    Stack, Chip, IconButton, Tooltip, Skeleton, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Button, Alert, InputAdornment,
    Box, Checkbox, FormControlLabel, Divider, useMediaQuery,
    Card, CardContent, Grid, Avatar, Collapse, List, ListItem,
    ListItemIcon, ListItemText, Accordion, AccordionSummary,
    AccordionDetails
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    ToggleOff, ToggleOn, Edit, LockReset, Delete,
    AccountBalance, MonetizationOn, Email, Person, CalendarToday,
    ExpandMore, Phone, Business, LocationOn, ChevronRight,
    CheckCircle, Cancel, AdminPanelSettings, AccountCircle,
    AttachMoney
} from '@mui/icons-material';
import axios from 'axios';

const roleColor = (r) => (r === 'admin' ? 'error' : r === 'comptable' ? 'info' : 'primary');
const roleIcon = (r) => {
    switch(r) {
        case 'admin': return <AdminPanelSettings />;
        case 'comptable': return <AttachMoney />;
        default: return <AccountCircle />;
    }
};

const statusChip = (b) => ({
    label: b ? 'Actif' : 'Inactif',
    color: b ? 'success' : 'error',
    icon: b ? <CheckCircle fontSize="small" /> : <Cancel fontSize="small" />
});

const makeIdemKey = (userId) => {
    try {
        if (window?.crypto?.randomUUID) return `addpts:${userId}:${window.crypto.randomUUID()}`;
    } catch {}
    return `addpts:${userId}:${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const PointsDisplay = ({ points, variant = 'default' }) => {
    const isPositive = points >= 0;
    const isSmall = variant === 'small';
    
    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: isPositive ? 'success.50' : 'error.50',
                color: isPositive ? 'success.main' : 'error.main',
                px: isSmall ? 1 : 1.5,
                py: isSmall ? 0.5 : 0.75,
                borderRadius: 1,
                border: 1,
                borderColor: isPositive ? 'success.100' : 'error.100',
                minWidth: isSmall ? 50 : 60,
                justifyContent: 'center'
            }}
        >
            <AttachMoney fontSize={isSmall ? "small" : "inherit"} />
            <Typography
                variant={isSmall ? "body2" : "body1"}
                sx={{
                    fontWeight: 'bold',
                    fontSize: isSmall ? '0.875rem' : '1rem'
                }}
            >
                {points}
            </Typography>
        </Box>
    );
};

// Composant pour afficher un utilisateur en mode carte détaillée
const UserCard = memo(({ 
    user, 
    onToggle, 
    onEdit, 
    onReset, 
    onDelete, 
    onOpenAdd 
}) => {
    const theme = useTheme();
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [expanded, setExpanded] = useState(false);

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Card sx={{ 
            mb: 2, 
            borderRadius: 2,
            borderLeft: 4,
            borderLeftColor: roleColor(user.role),
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
            <CardContent sx={{ p: 2 }}>
                {/* En-tête principale */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <Avatar sx={{ 
                        bgcolor: roleColor(user.role), 
                        width: 48, 
                        height: 48,
                        mr: 2,
                        fontSize: '1.2rem'
                    }}>
                        {user.prenom?.[0]}{user.nom?.[0]}
                    </Avatar>
                    
                    <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mr: 1 }}>
                                {user.prenom} {user.nom}
                            </Typography>
                            <Chip
                                {...statusChip(user.isActive)}
                                size="small"
                                icon={statusChip(user.isActive).icon}
                                sx={{ 
                                    height: 20,
                                    '& .MuiChip-icon': { fontSize: 12 }
                                }}
                            />
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            <Chip
                                label={user.role}
                                color={roleColor(user.role)}
                                size="small"
                                icon={roleIcon(user.role)}
                                sx={{ 
                                    textTransform: 'capitalize',
                                    '& .MuiChip-icon': { fontSize: 16 }
                                }}
                            />
                            
                            <PointsDisplay points={typeof user.points === 'number' ? user.points : 0} />

                        </Box>
                        
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            bgcolor: 'grey.50',
                            p: 1,
                            borderRadius: 1,
                            mt: 1
                        }}>
                            <Email sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                {user.email}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Section dépliante pour plus d'informations */}
                <Accordion 
                    expanded={expanded} 
                    onChange={() => setExpanded(!expanded)}
                    sx={{ 
                        mb: 1,
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        bgcolor: 'transparent'
                    }}
                >
                    <AccordionSummary 
                        expandIcon={<ExpandMore />}
                        sx={{ 
                            minHeight: 36,
                            py: 0,
                            '& .MuiAccordionSummary-content': { my: 0 }
                        }}
                    >
                        <Typography variant="button" color="primary">
                            Détails supplémentaires
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                        <List dense sx={{ py: 0 }}>
                            <ListItem sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <CalendarToday color="action" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Date de création"
                                    secondary={formatDate(user.createdAt)}
                                />
                            </ListItem>
                            
                            {user.lastLogin && (
                                <ListItem sx={{ px: 0 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <CheckCircle color="action" />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary="Dernière connexion"
                                        secondary={formatDate(user.lastLogin)}
                                    />
                                </ListItem>
                            )}
                            
                            <ListItem sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <AttachMoney color="action" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Solde actuel"
                                    secondary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PointsDisplay 
                                                points={typeof user.points === 'number' ? user.points : 0} 
                                                variant="small"
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                                points disponibles
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </ListItem>
                        </List>
                    </AccordionDetails>
                </Accordion>

                {/* Actions */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                    pt: 1,
                    borderTop: 1,
                    borderColor: 'divider'
                }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {user.role === 'comptable' && (
                            <Tooltip title="Modifier les points">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<MonetizationOn />}
                                    onClick={() => onOpenAdd(user)}
                                    sx={{ 
                                        borderRadius: 2,
                                        textTransform: 'none'
                                    }}
                                >
                                    Points
                                </Button>
                            </Tooltip>
                        )}
                        
                        <Tooltip title={user.isActive ? 'Désactiver' : 'Activer'}>
                            <Button
                                size="small"
                                variant="outlined"
                                color={user.isActive ? 'error' : 'success'}
                                startIcon={user.isActive ? <ToggleOff /> : <ToggleOn />}
                                onClick={() => onToggle(user)}
                                sx={{ 
                                    borderRadius: 2,
                                    textTransform: 'none'
                                }}
                            >
                                {user.isActive ? 'Désactiver' : 'Activer'}
                            </Button>
                        </Tooltip>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Modifier">
                            <IconButton
                                size="small"
                                onClick={() => onEdit(user)}
                                sx={{
                                    bgcolor: 'primary.50',
                                    '&:hover': { bgcolor: 'primary.100' }
                                }}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Réinitialiser le mot de passe">
                            <IconButton
                                size="small"
                                onClick={() => onReset(user)}
                                sx={{
                                    bgcolor: 'warning.50',
                                    '&:hover': { bgcolor: 'warning.100' }
                                }}
                            >
                                <LockReset fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Supprimer">
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => onDelete(user)}
                                sx={{
                                    bgcolor: 'error.50',
                                    '&:hover': { bgcolor: 'error.100' }
                                }}
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
});

// Skeleton pour mobile
const MobileSkeleton = () => (
    <Box>
        {[...Array(3)].map((_, i) => (
            <Card key={i} sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Skeleton variant="text" width="60%" height={28} sx={{ mr: 1 }} />
                                <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: 10 }} />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                <Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 12 }} />
                                <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 12 }} />
                                <Skeleton variant="text" width="30%" height={20} />
                            </Box>
                            <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
                        </Box>
                    </Box>
                    <Skeleton variant="text" width="40%" height={32} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                        <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 16 }} />
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {[...Array(3)].map((_, j) => (
                                <Skeleton key={j} variant="circular" width={36} height={36} />
                            ))}
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        ))}
    </Box>
);

function UsersTable({
    rows,
    loading,
    sortBy,
    order,
    onSort,
    onToggle,
    onEdit,
    onReset,
    onDelete,
    onRowUpdated = () => {},
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const columns = useMemo(() => ([
        { key: 'email', label: 'Email', width: '22%' },
        {
            key: 'fullName',
            label: 'Nom',
            map: (u) => `${u.prenom} ${u.nom}`,
            sortKey: 'nom',
            width: '18%'
        },
        { key: 'role', label: 'Rôle', width: '12%' },
        { key: 'isActive', label: 'Status', width: '12%' },
        { key: 'points', label: 'Solde', sortable: false, width: '12%' },
        { key: 'createdAt', label: 'Créé le', width: '14%' },
        { key: 'actions', label: 'Actions', sortable: false, width: '10%' },
    ]), []);

    // Dialog "Modifier les points"
    const [openFor, setOpenFor] = useState(null);
    const [pts, setPts] = useState(50);
    const [desc, setDesc] = useState('');
    const [isReduction, setIsReduction] = useState(false);
    const [allowNegative, setAllowNegative] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const parsedPts = Number(pts);
    const safePts = Number.isFinite(parsedPts) ? parsedPts : 0;

    const currentBalance = typeof openFor?.points === 'number' ? openFor.points : 0;
    const delta = isReduction ? -safePts : safePts;
    const nextBalance = currentBalance + delta;
    const wouldGoNegative = nextBalance < 0;
    const nextBalanceIsValid = !wouldGoNegative || allowNegative;

    const handleOpenAdd = (u) => {
        setOpenFor(u);
        setPts(50);
        setDesc('');
        setIsReduction(false);
        setAllowNegative(false);
        setErrorMsg('');
    };

    const handleCloseAdd = () => {
        if (submitting) return;
        setOpenFor(null);
        setPts(50);
        setDesc('');
        setIsReduction(false);
        setAllowNegative(false);
        setErrorMsg('');
    };

    const [shouldFocusPts, setShouldFocusPts] = useState(false);
    useEffect(() => {
        if (openFor) setShouldFocusPts(true);
    }, [openFor]);

    const handleSubmitAdd = async () => {
        if (!openFor?._id) return;

        const pointsAbs = Number(pts);

        if (!Number.isInteger(pointsAbs) || pointsAbs <= 0) {
            setErrorMsg('Le nombre de points doit être un entier positif > 0');
            return;
        }

        const pointsToApply = isReduction ? -pointsAbs : pointsAbs;

        const finalDesc = String(desc).trim() ||
            (isReduction ? `Retrait de ${pointsAbs} points` : `Ajout de ${pointsAbs} points`);

        const newBalance = (typeof openFor.points === 'number' ? openFor.points : 0) + pointsToApply;
        if (newBalance < 0 && !allowNegative) {
            setErrorMsg(`Le retrait de ${pointsAbs} points ferait passer le solde à ${newBalance}. Cochez "Autoriser le solde négatif" pour forcer l'opération.`);
            return;
        }

        setSubmitting(true);
        setErrorMsg('');

        try {
            const idempotencyKey = makeIdemKey(openFor._id);
            const { data } = await axios.post(`/api/admin/users/${openFor._id}/add-points`, {
                points: pointsToApply,
                description: finalDesc,
                idempotencyKey,
                allowNegative,
            });

            if (data?.user) onRowUpdated(data.user);

            handleCloseAdd();
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || 'Erreur inconnue';
            setErrorMsg(msg);
        } finally {
            setSubmitting(false);
        }
    };

    // Vue mobile (cartes détaillées)
    if (isMobile) {
        return (
            <>
                {loading ? (
                    <MobileSkeleton />
                ) : rows.length === 0 ? (
                    <Box sx={{ 
                        py: 6, 
                        textAlign: 'center',
                        bgcolor: 'grey.50',
                        borderRadius: 2
                    }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Aucun utilisateur trouvé
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Aucun utilisateur ne correspond à vos critères de recherche
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        {rows.map((u) => (
                            <UserCard
                                key={u._id}
                                user={u}
                                onToggle={onToggle}
                                onEdit={onEdit}
                                onReset={onReset}
                                onDelete={onDelete}
                                onOpenAdd={handleOpenAdd}
                            />
                        ))}
                    </Box>
                )}

                {/* Dialog Modifier les points */}
                <Dialog
                    open={!!openFor}
                    onClose={handleCloseAdd}
                    fullWidth
                    maxWidth="sm"
                    fullScreen={isSmallMobile}
                    PaperProps={{
                        sx: {
                            borderRadius: isSmallMobile ? 0 : 2,
                            overflow: 'hidden',
                            maxWidth: isSmallMobile ? '100%' : '500px'
                        }
                    }}
                >
                    <DialogTitle
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            bgcolor: 'primary.50',
                            py: 2,
                            px: isSmallMobile ? 2 : 3
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <AccountBalance color="primary" />
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h6">
                                    Modifier les points
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ 
                                    wordBreak: 'break-word',
                                    fontSize: isSmallMobile ? '0.875rem' : '1rem'
                                }}>
                                    {openFor ? `${openFor.prenom} ${openFor.nom}` : ''}
                                </Typography>
                            </Box>
                        </Stack>
                    </DialogTitle>

                    <DialogContent
                        sx={{
                            py: 2.5,
                            px: isSmallMobile ? 2 : 3
                        }}
                    >
                        {errorMsg && (
                            <Alert
                                severity="error"
                                sx={{ mb: 2 }}
                                onClose={() => setErrorMsg('')}
                            >
                                {errorMsg}
                            </Alert>
                        )}

                        <Stack spacing={2}>
                            {/* Résumé du solde */}
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: 'background.paper'
                                }}
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Solde actuel
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AttachMoney color="primary" />
                                            <Typography variant="h5">
                                                {currentBalance}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                pts
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Nouveau solde
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AttachMoney color={nextBalanceIsValid ? "success" : "error"} />
                                            <Typography 
                                                variant="h5" 
                                                color={nextBalanceIsValid ? "success.main" : "error.main"}
                                            >
                                                {nextBalance}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                pts
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 2 }} />

                                {/* Champ points */}
                                <TextField
                                    label="Nombre de points"
                                    type="number"
                                    inputProps={{ min: 1, step: 1 }}
                                    value={pts}
                                    onChange={(e) => setPts(e.target.value)}
                                    size="small"
                                    fullWidth
                                    autoFocus={shouldFocusPts}
                                    onFocus={() => setShouldFocusPts(false)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AttachMoney />
                                            </InputAdornment>
                                        ),
                                        endAdornment: <InputAdornment position="end">points</InputAdornment>
                                    }}
                                />

                                {/* Toggle réduction */}
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isReduction}
                                            onChange={(e) => setIsReduction(e.target.checked)}
                                            color={isReduction ? "error" : "primary"}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {isReduction ? (
                                                <>
                                                    <Typography color="error.main" fontWeight="medium">
                                                        Retirer des points
                                                    </Typography>
                                                    <Typography variant="caption" color="error.main">
                                                        (-{safePts} points)
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography color="success.main" fontWeight="medium">
                                                        Ajouter des points
                                                    </Typography>
                                                    <Typography variant="caption" color="success.main">
                                                        (+{safePts} points)
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                    }
                                    sx={{ mt: 1 }}
                                />

                                {/* Autorisation explicite solde négatif (visible seulement si retrait > solde) */}
                                {isReduction && wouldGoNegative && (
                                    <Box sx={{ mt: 1, p: 1.5, borderRadius: 2, bgcolor: 'warning.50', border: 1, borderColor: 'warning.light' }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={allowNegative}
                                                    onChange={(e) => setAllowNegative(e.target.checked)}
                                                    color="warning"
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600} color="warning.dark">
                                                        Autoriser le solde négatif
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Le solde passera à {nextBalance} points (recouvrement / dette)
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </Box>
                                )}
                            </Paper>

                            {/* Description */}
                            <TextField
                                label="Description (optionnel)"
                                value={desc}
                                onChange={(e) => setDesc(e.target.value)}
                                size="small"
                                multiline
                                rows={2}
                                fullWidth
                                helperText="Laissez vide pour une description automatique"
                                placeholder="Raison de la modification..."
                            />
                        </Stack>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            px: isSmallMobile ? 2 : 3,
                            py: 2,
                            borderTop: 1,
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            gap: 1,
                            flexDirection: isSmallMobile ? 'column' : 'row'
                        }}
                    >
                        <Button
                            onClick={handleCloseAdd}
                            disabled={submitting}
                            color="inherit"
                            variant="outlined"
                            fullWidth={isSmallMobile}
                            sx={{ 
                                borderRadius: 2,
                                textTransform: 'none'
                            }}
                        >
                            Annuler
                        </Button>

                        <Button
                            onClick={handleSubmitAdd}
                            variant="contained"
                            disabled={submitting || !Number.isInteger(Number(pts)) || Number(pts) <= 0 || !nextBalanceIsValid}
                            color={isReduction ? 'error' : 'success'}
                            fullWidth={isSmallMobile}
                            sx={{ 
                                borderRadius: 2,
                                textTransform: 'none',
                                minWidth: isSmallMobile ? '100%' : 180
                            }}
                        >
                            {submitting ? 'Traitement...' : (
                                isReduction ? `Retirer ${safePts} points` : `Ajouter ${safePts} points`
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }

    // Vue desktop (tableau)
    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                }}
            >
                <Table size="medium">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            {columns.map(col => (
                                <TableCell
                                    key={col.key}
                                    onClick={() => col.sortable === false ? null : onSort(col.sortKey || col.key)}
                                    sx={{
                                        cursor: col.sortable === false ? 'default' : 'pointer',
                                        whiteSpace: 'nowrap',
                                        width: col.width,
                                        py: 2,
                                        borderBottom: 2,
                                        borderColor: 'divider',
                                        '&:hover': col.sortable !== false ? { bgcolor: 'action.hover' } : {}
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Typography variant="subtitle2" color="text.primary" fontWeight={600}>
                                            {col.label}
                                        </Typography>
                                        {col.sortable === false ? null : (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    opacity: sortBy === (col.sortKey || col.key) ? 1 : 0.3,
                                                    color: sortBy === (col.sortKey || col.key) ? 'primary.main' : 'text.secondary'
                                                }}
                                            >
                                                {sortBy === (col.sortKey || col.key)
                                                    ? (order === 'asc' ? '▲' : '▼')
                                                    : '⇅'}
                                            </Typography>
                                        )}
                                    </Stack>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading ? (
                            [...Array(6)].map((_, i) => (
                                <TableRow key={`sk-${i}`} hover>
                                    <TableCell colSpan={7} sx={{ py: 3 }}>
                                        <Skeleton height={48} />
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} sx={{ py: 6 }}>
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography variant="h6" color="text.secondary" gutterBottom>
                                            Aucun utilisateur trouvé
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Essayez de modifier vos critères de recherche
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((u) => (
                                <TableRow
                                    key={u._id}
                                    hover
                                    sx={{ 
                                        '&:hover': { bgcolor: 'action.hover' },
                                        transition: 'background-color 0.2s'
                                    }}
                                >
                                    <TableCell sx={{ py: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Avatar sx={{ 
                                                width: 32, 
                                                height: 32, 
                                                mr: 1.5,
                                                bgcolor: roleColor(u.role),
                                                fontSize: '0.875rem'
                                            }}>
                                                {u.prenom?.[0]}
                                            </Avatar>
                                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                {u.email}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ py: 2 }}>
                                        <Typography variant="body2" fontWeight="medium">
                                            {`${u.prenom} ${u.nom}`}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 2 }}>
                                        <Chip
                                            label={u.role}
                                            color={roleColor(u.role)}
                                            size="small"
                                            icon={roleIcon(u.role)}
                                            sx={{ 
                                                fontWeight: 'medium', 
                                                textTransform: 'capitalize',
                                                '& .MuiChip-icon': { fontSize: 16 }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ py: 2 }}>
                                        <Chip
                                            {...statusChip(u.isActive)}
                                            size="small"
                                            icon={statusChip(u.isActive).icon}
                                            sx={{ 
                                                fontWeight: 'medium', 
                                                minWidth: 90,
                                                '& .MuiChip-icon': { fontSize: 14 }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ py: 2 }}>
                                        <PointsDisplay points={typeof u.points === 'number' ? u.points : 0} />
                                    </TableCell>
                                    <TableCell sx={{ py: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {u.createdAt ? new Date(u.createdAt).toLocaleDateString('fr-FR', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric'
                                            }) : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                        <Stack direction="row" spacing={0.5}>
                                            {u.role === 'comptable' && (
                                                <Tooltip title="Modifier les points">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleOpenAdd(u)}
                                                        sx={{
                                                            bgcolor: 'primary.50',
                                                            '&:hover': { bgcolor: 'primary.100' }
                                                        }}
                                                    >
                                                        <MonetizationOn />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            <Tooltip title={u.isActive ? 'Désactiver' : 'Activer'}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onToggle(u)}
                                                    color={u.isActive ? 'error' : 'success'}
                                                    sx={{
                                                        bgcolor: u.isActive ? 'error.50' : 'success.50',
                                                        '&:hover': { bgcolor: u.isActive ? 'error.100' : 'success.100' }
                                                    }}
                                                >
                                                    {u.isActive ? <ToggleOff /> : <ToggleOn />}
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Modifier">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onEdit(u)}
                                                    sx={{
                                                        bgcolor: 'action.selected',
                                                        '&:hover': { bgcolor: 'action.hover' }
                                                    }}
                                                >
                                                    <Edit />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Réinitialiser le mot de passe">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onReset(u)}
                                                    sx={{
                                                        bgcolor: 'warning.50',
                                                        '&:hover': { bgcolor: 'warning.100' }
                                                    }}
                                                >
                                                    <LockReset />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Supprimer">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => onDelete(u)}
                                                    sx={{
                                                        bgcolor: 'error.50',
                                                        '&:hover': { bgcolor: 'error.100' }
                                                    }}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Paper>

            {/* Dialog Modifier les points (desktop) */}
            <Dialog
                open={!!openFor}
                onClose={handleCloseAdd}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: 'primary.50',
                        py: 3
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <AccountBalance color="primary" />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h5">
                                Modifier les points
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {openFor ? `${openFor.prenom} ${openFor.nom} • ${openFor.email}` : ''}
                            </Typography>
                        </Box>
                    </Stack>
                </DialogTitle>

                <DialogContent
                    sx={{
                        py: 3
                    }}
                >
                    {errorMsg && (
                        <Alert
                            severity="error"
                            sx={{ mb: 3 }}
                            onClose={() => setErrorMsg('')}
                        >
                            {errorMsg}
                        </Alert>
                    )}

                    <Stack spacing={3}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                bgcolor: 'background.paper'
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={3}
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Solde actuel
                                    </Typography>
                                    <Typography variant="h4" sx={{ lineHeight: 1.2 }}>
                                        {currentBalance}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        points
                                    </Typography>
                                </Box>

                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Nouveau solde
                                    </Typography>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            lineHeight: 1.2,
                                            color: nextBalanceIsValid ? 'success.main' : 'error.main'
                                        }}
                                    >
                                        {nextBalance}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        points
                                    </Typography>
                                </Box>
                            </Stack>

                            <Divider sx={{ my: 3 }} />

                            <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                            >
                                <TextField
                                    label="Nombre de points"
                                    type="number"
                                    inputProps={{ min: 1, step: 1 }}
                                    value={pts}
                                    onChange={(e) => setPts(e.target.value)}
                                    size="medium"
                                    sx={{ flexGrow: 1 }}
                                    autoFocus={shouldFocusPts}
                                    onFocus={() => setShouldFocusPts(false)}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">pts</InputAdornment>
                                    }}
                                />

                                <FormControlLabel
                                    sx={{
                                        m: 0,
                                        px: 2,
                                        py: 1,
                                        borderRadius: 2,
                                        bgcolor: isReduction ? 'error.50' : 'action.hover',
                                        '&:hover': { bgcolor: isReduction ? 'error.100' : 'action.selected' },
                                        '& .MuiFormControlLabel-label': {
                                            ml: 1,
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                            color: isReduction ? 'error.main' : 'text.primary'
                                        }
                                    }}
                                    control={
                                        <Checkbox
                                            checked={isReduction}
                                            onChange={(e) => setIsReduction(e.target.checked)}
                                            color={isReduction ? 'error' : 'primary'}
                                        />
                                    }
                                    label="Réduire des points"
                                />
                            </Stack>

                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                Impact :{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        fontWeight: 700,
                                        color: isReduction ? 'error.main' : 'success.main'
                                    }}
                                >
                                    {isReduction ? `-${safePts}` : `+${safePts}`}
                                </Box>
                                {' '}points
                            </Typography>

                            {/* Autorisation explicite solde négatif (visible seulement si retrait > solde) */}
                            {isReduction && wouldGoNegative && (
                                <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'warning.50', border: 1, borderColor: 'warning.light' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={allowNegative}
                                                onChange={(e) => setAllowNegative(e.target.checked)}
                                                color="warning"
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body2" fontWeight={600} color="warning.dark">
                                                    Autoriser le solde négatif
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Le solde passera à {nextBalance} points (recouvrement / dette)
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Box>
                            )}
                        </Paper>

                        <TextField
                            label="Description (optionnel)"
                            value={desc}
                            onChange={(e) => setDesc(e.target.value)}
                            size="medium"
                            multiline
                            rows={3}
                            fullWidth
                            helperText="Laissez vide pour une description automatique"
                            placeholder="Décrivez la raison de cette modification..."
                        />
                    </Stack>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        py: 3,
                        borderTop: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        gap: 2
                    }}
                >
                    <Button
                        onClick={handleCloseAdd}
                        disabled={submitting}
                        color="inherit"
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            px: 4
                        }}
                    >
                        Annuler
                    </Button>

                    <Button
                        onClick={handleSubmitAdd}
                        variant="contained"
                        disabled={submitting || !Number.isInteger(Number(pts)) || Number(pts) <= 0 || !nextBalanceIsValid}
                        color={isReduction ? 'error' : 'success'}
                        sx={{
                            borderRadius: 2,
                            px: 4,
                            minWidth: 200
                        }}
                    >
                        {submitting ? 'Traitement...' : (isReduction ? 'Retirer les points' : 'Ajouter les points')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default memo(UsersTable);